import React, { useState, useRef, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * FileUploader - File upload component
 * 
 * @param {Object} props
 * @param {string} props.accept - Accepted file types, default ".pdf"
 * @param {number} props.maxSize - Max file size in bytes, default 50MB
 * @param {string} props.title - Upload area title
 * @param {string} props.subtitle - Upload area subtitle
 * @param {string} props.browseButtonText - Browse button text
 * @param {string} props.changeButtonText - Change file button text
 * @param {string} props.retryButtonText - Retry button text
 * @param {Array} props.requirements - File requirements list
 * @param {Function} props.onFileSelect - File selection callback
 * @param {Function} props.onContinue - Continue button callback
 * @param {Function} props.onCancel - Cancel button callback
 * @param {string} props.cancelButtonText - Cancel button text
 * @param {string} props.continueButtonText - Continue button text
 * @param {boolean} props.showActions - Whether to show bottom action buttons
 * @param {Function} props.validateFile - Custom file validation function
 */
const FileUploader = ({
  accept = '.pdf',
  maxSize = 50 * 1024 * 1024,
  title = 'Drag & drop your PDF file here',
  subtitle = 'or click to browse',
  browseButtonText = 'Browse Files',
  changeButtonText = 'Change File',
  retryButtonText = 'Choose Another File',
  requirements = [
    { label: 'Format: PDF only' },
    { label: 'Max size: 50 MB' },
    { label: 'Language: English contracts' },
  ],
  onFileSelect,
  onContinue,
  onCancel,
  cancelButtonText = 'Cancel',
  continueButtonText = 'Continue',
  showActions = true,
  validateFile: customValidate,
}) => {
  // State management
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Default file validation
  const defaultValidateFile = (file) => {
    // Check file type
    const acceptTypes = accept.split(',').map(t => t.trim());
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    const isValidType = acceptTypes.some(type => {
      if (type.startsWith('.')) {
        return fileExt === type.toLowerCase();
      }
      return file.type === type;
    });
    
    if (!isValidType) {
      return { valid: false, error: 'Invalid file format', message: `Please upload a valid file (${accept})` };
    }
    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      return { valid: false, error: `File size exceeds ${maxSizeMB}MB limit`, message: `Please upload a file smaller than ${maxSizeMB}MB` };
    }
    return { valid: true };
  };

  // Validate file
  const validateFile = customValidate || defaultValidateFile;

  // Handle file selection
  const handleFileSelect = useCallback((file) => {
    const validation = validateFile(file);
    if (validation.valid) {
      setSelectedFile(file);
      setError(null);
      onFileSelect?.(file);
    } else {
      setSelectedFile(null);
      setError(validation);
    }
  }, [validateFile, onFileSelect]);

  // Handle file input change
  const handleInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input to allow selecting same file
    e.target.value = '';
  };

  // Handle drag enter
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Trigger file selection
  const handleBrowseClick = (e) => {
    e?.stopPropagation();
    fileInputRef.current?.click();
  };

  // Cancel operation
  const handleCancel = () => {
    setSelectedFile(null);
    setError(null);
    onCancel?.();
  };

  // Continue operation
  const handleContinue = () => {
    if (selectedFile) {
      onContinue?.(selectedFile);
    }
  };

  // Render upload area content
  const renderUploadContent = () => {
    // Error state
    if (error) {
      return (
        <>
          {/* Error icon */}
          <div 
            className="w-14 h-14 rounded-lg mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-danger-light)' }}
          >
            <span className="text-2xl">⚠️</span>
          </div>
          
          {/* Error title */}
          <h3 
            className="text-xl font-semibold mb-2"
            style={{ color: 'var(--color-danger)' }}
          >
            {error.error}
          </h3>
          
          {/* Error description */}
          <p 
            className="mb-6"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {error.message}
          </p>
          
          {/* Retry button */}
          <button
            onClick={handleBrowseClick}
            className="px-6 py-3 font-medium rounded-xl transition-all duration-200 hover:shadow-md"
            style={{ 
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-button-primary-text)'
            }}
          >
            {retryButtonText}
          </button>
        </>
      );
    }

    // File selected state
    if (selectedFile) {
      return (
        <>
          {/* File icon - checkmark */}
          <div 
            className="w-14 h-14 rounded-lg mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-success-light)' }}
          >
            <span className="text-2xl text-white">✓</span>
          </div>
          
          {/* File name */}
          <h3 
            className="text-xl font-semibold mb-2"
            style={{ color: 'var(--color-text)' }}
          >
            {selectedFile.name}
          </h3>
          
          {/* File size */}
          <p 
            className="mb-6"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {formatFileSize(selectedFile.size)}
          </p>
          
          {/* Change file button */}
          <button
            onClick={handleBrowseClick}
            className="px-8 py-4 font-medium rounded-xl transition-all duration-200 hover:shadow-md"
            style={{ 
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-button-primary-text)'
            }}
          >
            {changeButtonText}
          </button>
        </>
      );
    }

    // Default state - ready to upload
    return (
      <>
        {/* File icon */}
        <div 
          className="w-14 h-14 rounded-lg mx-auto mb-6 flex items-center justify-center"
          style={{ backgroundColor: 'var(--color-primary-light)' }}
        >
          <span className="text-2xl">📄</span>
        </div>
        
        {/* Title */}
        <h3 
          className="text-xl font-semibold mb-2"
          style={{ color: 'var(--color-text)' }}
        >
          {title}
        </h3>
        
        {/* Subtitle */}
        <p 
          className="mb-6"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {subtitle}
        </p>
        
        {/* Browse file button */}
        <button
          onClick={handleBrowseClick}
          className="px-8 py-4 font-medium rounded-xl transition-all duration-200 hover:shadow-md"
          style={{ 
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-button-primary-text)'
          }}
        >
          {browseButtonText}
        </button>
      </>
    );
  };

  // Get upload area style based on state
  const getUploadAreaStyle = () => {
    // Error state
    if (error) {
      return {
        backgroundColor: 'var(--color-danger-light)',
        border: '2px solid var(--color-danger)',
      };
    }
    // File selected state
    if (selectedFile) {
      return {
        backgroundColor: 'var(--color-success-light)',
        border: '2px solid var(--color-success)',
      };
    }
    // Default state
    return {
      backgroundColor: 'var(--color-surface)',
      border: `2px dashed var(--color-border${isDragging ? '-dark' : ''})`,
    };
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Upload area */}
      <div
        className={`rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragging ? 'scale-[1.01]' : ''
        }`}
        style={getUploadAreaStyle()}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={!error && !selectedFile ? handleBrowseClick : undefined}
      >
        <div className="py-12">
          {renderUploadContent()}
        </div>
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
        />
      </div>

      {/* File requirements info */}
      {requirements && requirements.length > 0 && (
        <div 
          className="rounded-xl p-5"
          style={{ 
            backgroundColor: 'var(--color-surface)',
            border: '1px solid var(--color-border)'
          }}
        >
          <h4 
            className="font-semibold mb-3"
            style={{ color: 'var(--color-text)' }}
          >
            File Requirements
          </h4>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {requirements.map((req, index) => (
              <span 
                key={index}
                className="text-sm"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                • {req.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {showActions && (
        <div className="flex justify-between items-center">
          {/* Cancel button */}
          <button
            onClick={handleCancel}
            className="px-8 py-3 font-medium rounded-xl transition-all duration-200 hover:opacity-80"
            style={{ 
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)'
            }}
          >
            {cancelButtonText}
          </button>
          
          {/* Continue button */}
          <button
            onClick={handleContinue}
            disabled={!selectedFile}
            className={`inline-flex items-center gap-2 px-8 py-3 font-medium rounded-xl transition-all duration-200 ${
              selectedFile ? 'hover:shadow-md' : 'opacity-50 cursor-not-allowed'
            }`}
            style={{ 
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-button-primary-text)'
            }}
          >
            <span>{continueButtonText}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
